import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { readAnalytics, type AccountAnalyticsResponse } from "@/shared/api";
import { projectAccountAnalytics } from "@/shared/analytics/presentation";
import { validatePeriod, type SelectedPeriod } from "@/shared/period/period";

export const ALL_ACCOUNT_PERIOD: SelectedPeriod = {
  since: null,
  until: null,
  preset: "all",
};

export function accountAnalyticsKey(period: SelectedPeriod): readonly unknown[] {
  return ["analytics", period.since, period.until, period.preset];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

function isNullableString(value: unknown): boolean {
  return typeof value === "string" || value === null;
}

function isNullableNumeric(value: unknown): boolean {
  return typeof value === "number" || value === null;
}

function isStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

function isPoint(value: unknown): boolean {
  return isRecord(value) && typeof value.at === "string" && typeof value.value === "string";
}

function isSummary(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    isNullableString(value.average_net) &&
    isNullableNumeric(value.average_seconds) &&
    isNullableString(value.best) &&
    typeof value.capital_days === "string" &&
    typeof value.cycles === "number" &&
    typeof value.fees === "string" &&
    typeof value.funding === "string" &&
    typeof value.losers === "number" &&
    isNullableString(value.maximum_drawdown) &&
    isNullableString(value.owner_return) &&
    isNullableString(value.portfolio_pnl) &&
    isNullableString(value.profit_factor) &&
    typeof value.realised_net === "string" &&
    isNullableString(value.strategy_return) &&
    typeof value.winners === "number" &&
    isNullableString(value.winrate) &&
    isNullableString(value.worst)
  );
}

function isPool(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    isNullableString(value.average_net) &&
    isNullableNumeric(value.average_seconds) &&
    isNullableString(value.best) &&
    typeof value.capital_days === "string" &&
    typeof value.cycles === "number" &&
    typeof value.fees === "string" &&
    typeof value.funding === "string" &&
    typeof value.losers === "number" &&
    typeof value.pool === "string" &&
    isNullableString(value.profit_factor) &&
    typeof value.realised_net === "string" &&
    isStringArray(value.strategy_versions) &&
    typeof value.winners === "number" &&
    isNullableString(value.winrate) &&
    isNullableString(value.worst)
  );
}

function isAccountAnalyticsResponse(value: unknown): value is AccountAnalyticsResponse {
  if (!isRecord(value)) return false;
  return (
    isNullableNumeric(value.age_seconds) &&
    typeof value.as_of === "string" &&
    isStringArray(value.available_pools) &&
    isStringArray(value.available_strategy_versions) &&
    isNullableString(value.coverage_since) &&
    isNullableString(value.coverage_until) &&
    typeof value.covered === "boolean" &&
    isNullableString(value.effective_since) &&
    typeof value.effective_until === "string" &&
    typeof value.freshness_budget_seconds === "number" &&
    Array.isArray(value.portfolio) && value.portfolio.every(isPoint) &&
    Array.isArray(value.realised) && value.realised.every(isPoint) &&
    Array.isArray(value.pools) && value.pools.every(isPool) &&
    typeof value.stale === "boolean" &&
    isSummary(value.summary)
  );
}

/** How often a period's figures refresh while the page stays open.
 *  A period ending now changes with every close — today's PnL, the totals,
 *  the charts — so it refreshes with the rest of the page; a closed historical
 *  range does not change and is not re-read (audit A10). */
export const ANALYTICS_REFRESH_MS = 30_000;

export function analyticsRefreshInterval(
  period: SelectedPeriod,
  now: number,
): number | false {
  const validation = validatePeriod(period);
  if (validation.kind === "invalid") return false;
  if (validation.kind === "preset") return ANALYTICS_REFRESH_MS;
  return Date.parse(validation.until) > now ? ANALYTICS_REFRESH_MS : false;
}

/** The query behind one period's Analytics, for `useQuery` or `useQueries`. */
export function accountAnalyticsQuery(period: SelectedPeriod) {
  const validation = validatePeriod(period);
  return {
    queryKey: accountAnalyticsKey(period),
    enabled: validation.kind !== "invalid",
    retry: false,
    refetchInterval: () => analyticsRefreshInterval(period, Date.now()),
    queryFn: async (): Promise<AccountAnalyticsResponse> => {
      const { data, error, response } = await readAnalytics({
        query:
          validation.kind === "exact"
            ? { since: validation.since, until: validation.until }
            : { preset: period.preset === "custom" ? null : period.preset },
      });
      if (
        error !== undefined ||
        !isAccountAnalyticsResponse(data) ||
        projectAccountAnalytics(data) === null
      ) {
        throw Object.assign(new Error("analytics unreadable"), {
          status: response?.status,
        });
      }
      return data;
    },
  } as const;
}

/** One server-owned Analytics read shared by compact and detailed consumers. */
export function useAccountAnalytics(
  period: SelectedPeriod,
): UseQueryResult<AccountAnalyticsResponse> {
  return useQuery(accountAnalyticsQuery(period));
}
